import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { ChevronDown, ChevronRight, Plus, Trash2, Search, ToggleLeft, ToggleRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function CopyBankPanel() {
  const [contexts, setContexts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [newVar, setNewVar] = useState({});

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/copy-bank`);
      setContexts(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleActive = async (key, current) => {
    await axios.put(`${API}/admin/copy-bank/${key}`, { active: !current });
    fetch();
  };

  const addVariation = async (key) => {
    const text = (newVar[key] || "").trim();
    if (!text) return;
    const ctx = contexts.find((c) => c.context_key === key);
    if (!ctx) return;
    await axios.put(`${API}/admin/copy-bank/${key}`, {
      variations: [...ctx.variations, text],
    });
    setNewVar((p) => ({ ...p, [key]: "" }));
    fetch();
  };

  const removeVariation = async (key, idx) => {
    const ctx = contexts.find((c) => c.context_key === key);
    if (!ctx) return;
    const updated = ctx.variations.filter((_, i) => i !== idx);
    await axios.put(`${API}/admin/copy-bank/${key}`, { variations: updated });
    fetch();
  };

  const filtered = contexts.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      c.context_key.includes(s) ||
      c.goal?.toLowerCase().includes(s) ||
      c.variations?.some((v) => v.toLowerCase().includes(s))
    );
  });

  const totalVariations = contexts.reduce((n, c) => n + (c.variations?.length || 0), 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="copy-bank-panel">
      <div className="px-4 pt-4 pb-3 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-slate-800">Copy Bank</h2>
          <p className="text-[10px] text-slate-400">{contexts.length} contexts / {totalVariations} variations</p>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="pl-8 pr-3 py-1.5 text-[11px] rounded-lg border border-slate-200 w-40"
            data-testid="copy-bank-search"
          />
        </div>
      </div>

      <div className="max-h-[500px] overflow-y-auto" data-testid="copy-bank-list">
        {filtered.map((ctx) => {
          const isOpen = expanded === ctx.context_key;
          return (
            <div key={ctx.context_key} className="border-t border-slate-100">
              <button
                onClick={() => setExpanded(isOpen ? null : ctx.context_key)}
                className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-slate-50/50"
                data-testid={`cb-row-${ctx.context_key}`}
              >
                {isOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                <span className="text-[11px] font-semibold text-slate-700 flex-1">{ctx.context_key}</span>
                <span className="text-[10px] text-slate-400">{ctx.variations?.length || 0} vars</span>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleActive(ctx.context_key, ctx.active); }}
                  className="ml-2"
                  data-testid={`cb-toggle-${ctx.context_key}`}
                >
                  {ctx.active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4 text-slate-300" />}
                </button>
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-3 space-y-1.5">
                      <p className="text-[10px] text-slate-500 italic">{ctx.goal}</p>
                      {ctx.variations?.map((v, i) => (
                        <div key={i} className="flex items-center gap-2 group">
                          <span className="text-[11px] text-slate-600 flex-1">{i + 1}. {v}</span>
                          <button
                            onClick={() => removeVariation(ctx.context_key, i)}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition"
                            data-testid={`cb-del-${ctx.context_key}-${i}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          value={newVar[ctx.context_key] || ""}
                          onChange={(e) => setNewVar((p) => ({ ...p, [ctx.context_key]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && addVariation(ctx.context_key)}
                          placeholder="Add new variation..."
                          className="flex-1 text-[11px] px-2.5 py-1.5 border border-slate-200 rounded-lg"
                          data-testid={`cb-input-${ctx.context_key}`}
                        />
                        <button
                          onClick={() => addVariation(ctx.context_key)}
                          className="px-2 py-1.5 rounded-lg bg-slate-800 text-white text-[10px] font-bold hover:bg-slate-700"
                          data-testid={`cb-add-${ctx.context_key}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
